import {_, computed, jsonComputedOptions, observable, action, trace} from '@api'
import {FilterDefinition, vlpFilterDefinition} from '@api/graphql'
import {SeasonSelectorFilterDropdownStore} from '@modules'
import { RefetchType, ui, OFFLINE_ID, WorkspaceConfig, SeasonSelectorFieldConfig, onSiteError, CatalogStore, globalStores} from '@stores'
import {EsQueryClause} from '@stores/elasticsearch'
import {VLP_FOLDER_LENGTH_RUNTIME_MAPPING, VLP_FOLDER_LENGTH_RUNTIME_MAPPING_ID} from '@stores/vlp'
import {guid, removeUndefinedsNested} from '@utils'
import {computedFn} from 'mobx-utils'

export const EMPTY_FILTER = {query: {bool: {filter: []}}}
export const EMPTY_IGNORE = {ignore: {}}

export class CatalogElasticSearchStore {
  // fieldFor = computedFn((field: IBoardFieldConfig & { field: string }) => {
  //   return `${field.field}${field?.group?.field ? `.${field?.group?.field}` : ''}${field.def?.name === 'RefList' || field.keyword ? '.keyword' : ''}`
  // })

  constructor(public catalog: CatalogStore) {
  }

  get stores() { return this.catalog.stores}

  @computed.struct get defaultFilterTerms() {
    return this.filterTermsFor(null, null)
  }

  @observable lastRefetchType ?: RefetchType
  @observable refetchId = guid()
  @observable aggregateDetailsRefetchId = guid()

  @computed(jsonComputedOptions) get catalogItemFilter() {
    const {catalog, catalog: {c8}} = this
    if (!catalog.hasLoadedConfig || !c8.hasLoadedHierarchy) return {filter: EMPTY_FILTER}
    const {catalogItemFilterFor} = this
    const r = {filter: catalogItemFilterFor(EMPTY_IGNORE)}

    return r
  }

  @computed(jsonComputedOptions) get catalogItemLibrary() {
    if (!this.catalog.hasLoadedConfig) return null
    const {catalogItemFilterFor} = this
    // Todo - map catalog field to library field via foreign key (collectionVersion.name => name)
    let r = {filter: catalogItemFilterFor({ignore: {customAttributes: true, vlpProduct: true, vlpVersion: true, search: true}})}

    return r
  }

  @computed get myShouldTermForInlineItemsFirst() {
    const {catalog: {localLODCatalogItems}} = this
    return {
      should: {
        bool: {
          must: {
            terms: {
              id: localLODCatalogItems.filter(f => !f.includes(OFFLINE_ID))
            }
          }
        }
      }
    }
  }

  catalogItemFilterFor = ({ignore}: { ignore?: Partial<vlpFilterDefinition & FilterDefinition & { search: boolean }> }) => {
    const {catalog} = this
    try {
      const {stores: {workspaces: {view}}} = catalog
      if (view == 'catalog') {
        return this.filterTermsFor(ignore)
      }
    } catch (err) {
      onSiteError(err)
    }
    // todo - seasonal library queries will go here
    return EMPTY_FILTER
  }

  @computed(jsonComputedOptions) get catalogSeasonalTerms() {
    const {workspaceConfig} = this.catalog
    return this.catalogSeasonalTermsFor({}, !workspaceConfig?.seasonIndex)
  }

  @action findSeasonalField: (key: string) => boolean = (key) => {
    const {catalog: {seasonSelectorFields}} = this
    return !!seasonSelectorFields.find(s => s.field === key)
  }

  termFieldFor = computedFn((field: string, attr: string) => {
    const {
      catalog: {_fieldByName}
    } = this

    const f = field == 'folder' ? 'subCategories' : field
    return `${f}.${attr}` + (_fieldByName[f]?.keyword ? '.keyword' : '')
  }, {name: `getTermField`})

  catalogSeasonalTermsFor = computedFn((ignore: Partial<vlpFilterDefinition & FilterDefinition>, devHierarchy: boolean): Array<any> => {
    const {
        catalog: {
          vlp,
          filter: {state: {groups}},
          stores: {c8}, seasonSelectors
        }
      }= this,
      season = groups.season ?? groups.ParentSeason,
      category1 = groups.category1 ?? groups.Category1,
      category2 = groups.category2 ?? groups.Category2,
      collection = groups.collection ?? groups.Collection,
      {findSeasonalField, termFieldFor} = this,
      terms = [],
      isPlan = findSeasonalField('plan'),
      isCollection = findSeasonalField('collection')

    if (isPlan && isCollection) {
      if (!ignore?.vlpPlan && vlp) {
        const {hierarchy: {hierarchyTermsFor}} = vlp,
          hierarchyTerms = hierarchyTermsFor(ignore)

        if (!_.isEmpty(hierarchyTerms)) {
          terms.push({
            bool: {
              minimum_should_match: 1,
              should: hierarchyTerms.filter(f => f.id).map(({type, id}) => ({term: {[termFieldFor(type, 'id')]: id}}))
            }
          })
        }
      }
    }

    if (devHierarchy) {
      const selectedSeasons = Array.from<string>(season?.selected?.keys() || []),
        selectedCategory1s = Array.from<string>(category1?.selected?.keys() || []),
        selectedCategory2s = Array.from<string>(category2?.selected?.keys() || []),
        selectedCollections = Array.from<string>(collection?.selected?.keys() || []),
        {category1NameDict, category2NameDict, seasonNameDict, collectionNameDict, category1sDict, category2sDict, collectionById, seasonsDict} = c8.services.c8ForCurrentWorkspace ?? {}

      if (!ignore?.season && selectedSeasons?.length) {
        // const {idField} = seasonSelectorFieldsById['season']
        // if (!idField) {
        terms.push(((seasonSelectors.season ?? seasonSelectors.ParentSeason) as SeasonSelectorFilterDropdownStore)?.c8ItemIdsSearchClause)

        //   terms.push({terms: {'season.id': selectedSeasons.flatMap(name => season.groupedByValue[name]?.exId ?? [])}})
          // terms.push({terms: {'season.name': selectedSeasons}})
        // } else {
        //   terms.push(isBreadcrumbSourceC8
        //              ? {terms: {[`season.${idField}`]: _.flatMap(selectedSeasons.map(c => seasonsDict[c]?.id ?? seasonNameDict[c]?.map(c => c.id))).filter(_.identity)}}
        //              : {terms: {'season.name': selectedSeasons}})
        // }
      }

      if (!ignore?.category1 && selectedCategory1s?.length) {
        // const {idField} = seasonSelectorFieldsById['category1']
        // if (!idField) {
          terms.push(((seasonSelectors.category1 ?? seasonSelectors.Category1) as SeasonSelectorFilterDropdownStore)?.c8ItemIdsSearchClause)
        //{terms: {'category1.id': (seasonSelectors.category1 as SeasonSelectorFilterDropdownStore).c8ItemIds}}) //.selectedCategory1s.flatMap(name => category1.groupedByValue[name]?.exId ?? [])}})
          // terms.push({terms: {'category1.name': selectedCategory1s}})
        // } else {
        //   terms.push(isBreadcrumbSourceC8
        //              ? {terms: {[`category1.${idField}`]: _.flatMap(selectedCategory1s.map(c => category1sDict[c]?.id ?? category1NameDict[c]?.map(c => c.id))).filter(_.identity)}}
        //              : {terms: {'category1.name': selectedCategory1s}})
        // }
      }

      if (!ignore?.category2 && selectedCategory2s?.length) {
        terms.push(((seasonSelectors.category2 ?? seasonSelectors.Category2) as SeasonSelectorFilterDropdownStore)?.c8ItemIdsSearchClause)
        // const {idField} = seasonSelectorFieldsById['category2']
        // if (!idField) {
        //   terms.push({terms: {'category2.id': selectedCategory2s.flatMap(name => category2.groupedByValue[name]?.exId ?? [])}})
          //terms.push({terms: {'category2.name': selectedCategory2s}})
        // } else {
        //   terms.push(isBreadcrumbSourceC8
        //              ? {terms: {[`category2.${idField}`]: _.flatMap(selectedCategory2s.map(c => category2sDict[c]?.id ?? category2NameDict[c]?.map(c => c.id))).filter(_.identity)}}
        //              : {terms: {'category2.name': selectedCategory2s}})
        // }
      }

      if (!ignore?.collection && selectedCollections?.length) {
        terms.push(((seasonSelectors.collection ?? seasonSelectors.Collection) as SeasonSelectorFilterDropdownStore)?.c8ItemIdsSearchClause)

        // const {idField} = seasonSelectorFieldsById['collection']
        // if (!idField) {
        //   terms.push({terms: {'collection.id': selectedCollections.flatMap(name => collection.groupedByValue[name]?.exId ?? [])}})
          //   terms.push({terms: {'collection.name': selectedCollections}})
        // }
        // else {
        //   terms.push(isBreadcrumbSourceC8
        //              ? {terms: {[`collection.${idField}`]: _.flatMap(selectedCollections.map(c => collectionById[c]?.id ?? collectionNameDict[c]?.map(c => c.id))).filter(_.identity)}}
        //              : {terms: {'collection.name': selectedCollections}})
        // }
      }
    }

    return terms.filter(_.identity)
  }, {name: `catalogSeasonalTermsFor`, ...jsonComputedOptions})

  @computed get escapeString() {
    const {
      catalog: {settings: {activeSearchText}}
    } = this
    let toBeEscaped = '+&&||!(){}[]^"~*?:\\/' // ES Special characters // don't replace '-' it will be turned into space later
    let escapedString = ''
    if (activeSearchText) {
      let string = _.unescape(activeSearchText)
      for (let i = 0; i < string.length; i++) {
        if (toBeEscaped.indexOf(string.charAt(i)) > -1) {
          escapedString = escapedString.concat('\\')
        }
        escapedString = escapedString.concat(string.charAt(i))
      }
    } else {
      escapedString = _.unescape(activeSearchText)
    }

    return escapedString
  }

  @computed get queryString() {
    return this.escapeString.trim().split(' ').map(v => `*${v}*`).join(' AND ')
  }

  isAvailableHierarchyField = computedFn((key: string) => {
    const {workspaces: {workspaceConfig}} = this.stores
    return !!workspaceConfig?.seasonSelectors?.find((sel) => sel.fields.find((field: SeasonSelectorFieldConfig) => field.field === key))
  })

  seasonSelectorRestrictedTermsFor(workspace: Partial<WorkspaceConfig>) {
    if (!workspace) return []

    // Always filter down the queries by what plans the user can see
    // TODO - the SERVER should be the one doing this
    const {catalog, catalog: {vlp}} = this,
      planField = this.termFieldFor('plan', 'id')

    const seasonSelectorFields = catalog._seasonSelectorFields(workspace.seasonSelectors ?? [])
    // TODO - This is very slow on Gant and not useful - we should do plan restriction in a different way that doesn't rely on us loading merch collections first
    let r : Array<EsQueryClause> = [] //|| seasonSelectorFields.some(f => f.field == 'plan') && vlp && _.isArray(vlp.hierarchy.plans.plans) ? [{terms: {[planField]: vlp.hierarchy.plans.plans.map(p => p.id)}}] : []

    // YFTS-10966 Remove inactive plans
    const {hierarchy: {inactivePlans}} = vlp
    if (!_.isEmpty(inactivePlans)) {
      r.push({
        bool: {must_not: { terms: { ['plan.id']: inactivePlans.map(p => p.id)}}}
      })
    }

    return r
  }

  filterTermsFor: (ignore: Partial<vlpFilterDefinition & FilterDefinition>, extraTerms?) => any = (ignore: Partial<vlpFilterDefinition & FilterDefinition & { search: boolean }>, extraTerms?) => {
    const {catalog, authStore, filter, catalog: {groupBy: {vlpHierarchyGroups}}} = this.stores

    if (!catalog || !authStore.userIsAuthenticated) return EMPTY_FILTER

    const {
        filter: {state: {groups: {vlpProduct, merchSeason}}},
        catalog: {additionalFilterClauses, seasonSelectorsCatalog: ss, settings: {freezeIds, activeSearchText}, workspaceConfig, searchFieldsEs, perspective},
        es
      } = this.stores,
      {termFieldFor, isAvailableHierarchyField} = this

    ignore = ignore || {}
    let filterTerms: Array<any> = [
      ...additionalFilterClauses,
      ...(_.isArray(extraTerms) ? extraTerms : (extraTerms && [extraTerms]) || []),

      // Always filter down the queries by what plans the user can see
      // TODO - the SERVER should be the one doing this
      ...(ignore.plan && !ignore.seasonal ? [] : this.seasonSelectorRestrictedTermsFor(workspaceConfig)),

      !ignore.seasonal && [...this.catalogSeasonalTermsFor(ignore, !workspaceConfig?.seasonIndex)],

      //!ignore.collectionVersion && !ignore.seasonal && collectionVersion?.selected.size && {terms: {[termFieldFor('collectionVersion', 'name')]: Array.from(collectionVersion.selected.keys()).filter(_.identity)}},
      !ignore.merchSeason && merchSeason?.selected.size && {terms: {'merchSeason.name': Array.from(merchSeason.selected.keys())}},
      !ignore.vlpProduct && vlpProduct?.selected.size && {terms: {[termFieldFor('merchProduct', 'id')]: Array.from(vlpProduct.selected.keys())}},
      isAvailableHierarchyField('collectionVersion') && !ignore.vlpVersion && !ignore.collectionVersion && !ignore.seasonal && ss?.collectionVersion?.c8ItemIdsSearchClause ? [{...ss?.collectionVersion?.c8ItemIdsSearchClause}].flat(): null,
      perspective?.filter,
      //...filterExtraTerms.map(t => t.query) // Do this client side instead for performance reasons
    ].filter(v => v && !_.isEmpty(v))

    if (freezeIds?.length && !ignore?.selection && !catalog.isShowingAssortment) {
      filterTerms.push({
        terms: {
          'exId': freezeIds.map(catalog.parseIdFromPath)
        }
      })
    }

    let addVlpFolderRuntimeMapping = false
    vlpHierarchyGroups.forEach(hg => {
      // YFTS-10927
      // If we're grouped by say Folder 2, we need to completely eliminate any items that are not in that part of the hierarchy
      if (hg?.vlpType?.type === 'folder') {
        const {vlpType: {folderDepth}} = hg

        addVlpFolderRuntimeMapping = true

        filterTerms.push({
          range: {
            [VLP_FOLDER_LENGTH_RUNTIME_MAPPING_ID]: {gt: folderDepth}
          }
        })
      }
    })
    // if (!isGlobalStore && isCurrentWorkspace) {
    //   filterTerms.push(globalStores.catalog.es.catalogItemFilter?.filter?.query)
    // }
    /*
    NVS - what is the purpose of below?  Combine with isBreadcrumbSourceC8 && !ignore.vlpVersion && !ignore.seasonal && seasonSelectors?.every(s => s.type === 'merch') && c8ItemIdsSearchClause ? [{...c8ItemIdsSearchClause}] : null
    - seeing duplicate clauses in the query along with circuit_break_exceptions due to this stuff
    seasonSelectorsFields.forEach(({field}) => {
       if (!ignore[field] && groups[field]?.selected.size) {
         filterTerms.push(
           (isBreadcrumbSourceC8 && c8.services[`${field}NameDict`]) ?
             {terms: {[`${field}.id`]: _.flatMap(Array.from(groups[field]?.selected.keys()).filter(_.identity).map(name => c8.services[`${field}NameDict`][name]?.map(v => v.id))).filter(_.identity)}} :
             {terms: {[`${field}.name` + (_fieldByName[field]?.keyword ? '.keyword' : '')]: Array.from(groups[field]?.selected.keys()).filter(_.identity)}}
         )
       }
     })*/

    // if (!ignore.vlpSecondaries && vlpSecondaries?.selected.size) {
    //   //filterTerms.push({filterTerms: {'secondaryVersions.id': _.filter(Array.from(vlpSecondaries.selected.keys()), id => id != FILTER_UNASSIGNED_ID)}})

    //   // let vlpSecondariesValues              = _.filter(Array.from(vlpSecondaries.selected.keys()), id => id != FILTER_UNASSIGNED_ID)
    //   // let isUnassignedvlpSecondariesClicked = vlpSecondaries.selected.has(FILTER_UNASSIGNED_ID)
    // }

    // region Custom Attribute(s)
    if (ignore.customAttributes !== true) {
      filterTerms.push(...filter.filterTermsFor(ignore, {fromFilter: false}))
    }

    // if (!ignore?.deleted && ui.settings?.deleteInfo?.markAsDelete) {
    //   filterTerms.push({...es.filterForNonDeletedItems})
    // }

    // endregion

    const r = removeUndefinedsNested({
      query: {
        // function_score need to be added this place
        bool: {
          filter: filterTerms.filter(_.identity).flat(1),
          must: !ignore.search ? _.isEmpty(activeSearchText) ? undefined : {
            bool: {
              filter: {
                query_string: {
                  fuzziness: 'AUTO',
                  query: es.tokenizeQueryString(this.queryString),
                  fields: searchFieldsEs,
                  default_operator: 'AND'
                }
              }
            }
          } : undefined

          /*
          multi_match: {
              query: seasonalSearchText,
              fields: searchFieldsEs,
              type: 'phrase_prefix'
            }

           */
          // {
          //   bool: {
          //     minimum_should_match: 1,
          //     should: [
          //       {
          //         multi_match: {
          //           query: seasonalSearchText,
          //           fields: searchFieldsEs,
          //           type: 'phrase_prefix'
          //         }
          //       },
          //       {
          //         multi_match: {
          //           query: seasonalSearchText,
          //           fields: searchFieldsEs,
          //           type: 'cross_fields',
          //           operator: 'and',
          //           "analyzer": "nGram_analyzer"
          //         }
          //       }
          //         ]
          //   }
        }
      },
      runtime_mappings: !addVlpFolderRuntimeMapping ? undefined : VLP_FOLDER_LENGTH_RUNTIME_MAPPING
    })

    // console.log({r, ignore})
    // return Object.freeze(r)
    return r
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogElasticSearchStore.ts